import { useId, type ReactNode } from 'react';
import type { CardKind, Role } from '../game/types';

// Self-contained monoline icon pack for the card game (no external requests).
// All icons draw with currentColor so they inherit the neon accent of context.

const CARD_PATHS: Record<CardKind, ReactNode> = {
  // Spike — lightning bolt
  hack: <path d="M13 2 L5 13 h5 l-1 9 8-12 h-5 z" fill="currentColor" stroke="none" />,
  // Firewall — brick wall
  firewall: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="1.5" />
      <path d="M3 10h18M3 15h18M9 5v5M15 10v5M9 15v4M15 5v0M12 10v5" />
    </>
  ),
  // Cipher shield — shield with keyhole
  cipherShield: (
    <>
      <path d="M12 3 L20 6 v5 c0 5-3.5 8-8 10 -4.5-2-8-5-8-10 V6 z" />
      <circle cx="12" cy="11" r="1.6" />
      <path d="M12 12.6 V15" />
    </>
  ),
  // Rig — targeting scope
  rig: (
    <>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  // Overclock — gauge / speedometer
  overclock: (
    <>
      <path d="M4 17 A9 9 0 0 1 20 17" />
      <path d="M12 17 L16 9" />
      <circle cx="12" cy="17" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  // Longwave — broadcast antenna
  longwave: (
    <>
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <path d="M8.5 8.5 a5 5 0 0 0 0 7M15.5 8.5 a5 5 0 0 1 0 7" />
      <path d="M6 6 a9 9 0 0 0 0 12M18 6 a9 9 0 0 1 0 12" />
    </>
  ),
  // Raid — inbox with down arrow
  raid: (
    <>
      <path d="M4 14 v4 a1 1 0 0 0 1 1 h14 a1 1 0 0 0 1-1 v-4" />
      <path d="M12 3 v9 M8.5 9 L12 12.5 L15.5 9" />
    </>
  ),
  // Downtime — pause / stall
  downtime: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 9v6M14 9v6" />
    </>
  ),
  // Med-patch — cross in a rounded case
  medPatch: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  // Payday — coin with spark
  payday: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v10M9.5 9.5h4a1.8 1.8 0 0 1 0 3.6h-3a1.8 1.8 0 0 0 0 3.6h4" />
    </>
  ),
  // Duel — crossed blades
  duel: (
    <>
      <path d="M4 4 L15 15 M14 4 L4 14" />
      <path d="M15 15 l2 -2 3 3 -2 2 z" fill="currentColor" stroke="none" />
      <path d="M4 14 l2 2" />
    </>
  ),
  // EMP — radial burst
  emp: (
    <>
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M19 5l-3 3M8 16l-3 3" />
    </>
  ),
  // Virus — capsid with spikes
  virus: (
    <>
      <circle cx="12" cy="12" r="5" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
      <circle cx="10.5" cy="11" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="13.5" cy="13" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  // Siphon — hand grabbing a card (arrow pulling in)
  siphon: (
    <>
      <rect x="8" y="3" width="11" height="8" rx="1.5" transform="rotate(18 13 7)" />
      <path d="M3 20 h7 M6.5 16.5 L3 20 l3.5 3.5" />
    </>
  ),
  // Stim — syringe / injector
  stim: (
    <>
      <path d="M14 3 L21 10 M17.5 6.5 L19.5 4.5" />
      <path d="M4 20 L13 11 l0 0 3 3 L7 23 z" transform="translate(-1 -2)" />
      <path d="M9 9 l6 6" />
    </>
  ),
  // Trace — magnifier over a face (scan)
  trace: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.2 15.2 L21 21" />
      <path d="M8 10.5 h5 M10.5 8 v5" />
    </>
  ),
  // Air Gap — isolated node inside a broken connection
  airGap: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M3 12h4M17 12h4M7 7 4.5 4.5M17 7l2.5-2.5M7 17l-2.5 2.5M17 17l2.5 2.5" />
    </>
  ),
  // Kill Switch — power glyph with a hard cut
  killSwitch: (
    <>
      <path d="M12 3v8" />
      <path d="M7.2 6.7a8 8 0 1 0 9.6 0" />
      <path d="M4 20 20 4" />
    </>
  ),
  // Backchannel — two linked message packets
  backchannel: (
    <>
      <path d="M4 5h10v7H9l-3 3v-3H4z" />
      <path d="M12 13h8v6h-3l-2 2v-2h-3z" />
    </>
  ),
  // Field Patch — medical cross with outward pulse
  fieldPatch: (
    <>
      <path d="M9 4h6v5h5v6h-5v5H9v-5H4V9h5z" />
      <path d="M2 12h2M20 12h2" />
    </>
  ),
  // Cold Reboot — circular refresh around a snowflake core
  coldReboot: (
    <>
      <path d="M19 8a8 8 0 1 0 1 7M19 3v5h-5" />
      <path d="M12 8v8M8.5 10l7 4M15.5 10l-7 4" />
    </>
  ),
  // Packet Storm — packets rotating in a ring
  packetStorm: (
    <>
      <rect x="4" y="4" width="5" height="5" rx="1" />
      <rect x="15" y="15" width="5" height="5" rx="1" />
      <path d="M10 6h5l-2-2M14 18H9l2 2M18 10v3l2-2M6 14v-3l-2 2" />
    </>
  ),
};

const ROLE_PATHS: Record<Role, ReactNode> = {
  // Director — crown
  director: <path d="M4 8 L7 15 h10 l3 -7 -5 4 -3 -6 -3 6 z" />,
  // Security — shield
  security: <path d="M12 3 L20 6 v5 c0 5-3.5 8-8 10 -4.5-2-8-5-8-10 V6 z" />,
  // Rebel — flame
  rebel: (
    <path d="M12 3 c3 4 5 5 5 9 a5 5 0 0 1-10 0 c0-2 1-3 2-4 c0 1.5 1 2 2 2 c-1-3 0-5 1-7 z" />
  ),
  // Fixer — masked unknown
  fixer: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.5 a2.8 2.8 0 0 1 5.4 1 c0 2-2.6 2.2-2.6 4" />
      <circle cx="12" cy="17.3" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
};

export function CardIcon({ kind, size = 24 }: { kind: CardKind; size?: number }) {
  return (
    <svg
      className="ico"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {CARD_PATHS[kind]}
    </svg>
  );
}

/** The NEONFALL coin: a flat-top hex token with an embossed "N" monogram.
 * Self-contained SVG (its own gold gradient, not currentColor). A unique
 * gradient id per instance keeps multiple coins on one screen from colliding. */
export function CoinIcon({ size = 16 }: { size?: number }) {
  const g = useId();
  return (
    <svg
      className="coin-icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
    >
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffe79a" />
          <stop offset="0.5" stopColor="#ffc24d" />
          <stop offset="1" stopColor="#a86a17" />
        </linearGradient>
      </defs>
      {/* Face + neon rim */}
      <polygon
        points="22.5,12 17.25,21.09 6.75,21.09 1.5,12 6.75,2.91 17.25,2.91"
        fill={`url(#${g})`}
        stroke="#ffd700"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      {/* Inner bevel ring */}
      <polygon
        points="19.2,12 15.6,18.24 8.4,18.24 4.8,12 8.4,5.76 15.6,5.76"
        fill="none"
        stroke="#8a5510"
        strokeWidth="0.9"
        strokeLinejoin="round"
        opacity="0.55"
      />
      {/* Engraved "N" */}
      <path
        d="M9.4 15.2 V8.8 L14.6 15.2 V8.8"
        fill="none"
        stroke="#7a4a0c"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Google's four-colour "G" mark. Self-contained SVG (brand-mandated colours,
 * so it deliberately ignores currentColor) for the "Sign in with Google" button. */
export function GoogleGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden>
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

export function RoleIcon({ role, size = 24 }: { role: Role; size?: number }) {
  return (
    <svg
      className="ico"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {ROLE_PATHS[role]}
    </svg>
  );
}
